import React, { useContext, useState, useEffect } from 'react';
import {
  AuthenticationContext,
  ResetPassword
} from '../providers/AuthenticationProvider';
import Page, { ICrumb } from '../components/Page';
import ContentBox from '../components/ContentBox';
import '../localization/config';
import { useTranslation } from 'react-i18next';
import {
  AddProject,
  HomeContext,
  IProjectData
} from '../providers/HomeProvider';
import { Icon, Item, Button, Message } from 'semantic-ui-react';
import { FlexColumn, FlexRow } from '../layouts';
import AddProjectModal from '../components/AddProjectModal';
import ConfirmationModal from '../components/ConfirmationModal';
import ResetPasswordModal from '../components/ResetPasswordModal';
import { useNavigate } from 'react-router-dom';
import LoginModal from '../components/LoginModal';
import ForgotPasswordModal from '../components/ForgotPasswordModal';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import ProjectImage from '../components/ProjectImage';

export interface IHomeProps {
}

const Home = (_props: IHomeProps) => {
  const [loginOpen, setLoginOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [activeProject, setActiveProject] = useState<AddProject | undefined>();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);

  const {
    onLogin,
    onResetPassword,
    onForgotPassword,
    isAuthenticated,
    retryCount,
    loginFailed,
    projectManager,
    resetMessage,
    systemAdmin
  } = useContext(AuthenticationContext);

  const {
    projects,
    onGotoProject,
    onCreateProject,
    onUpdateProject,
    onDeleteProject,
    onSaveProjectImage
  } = useContext(HomeContext);

  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isAuthenticated()) {
      setLoginOpen(true);
    } else {
      setLoginOpen(false);
    }
  }, [retryCount, isAuthenticated]);

  const handleLogin = () => {
    onLogin(email, password);
  };

  const handleEdit = (item: IProjectData) => {
    setActiveProject({
      id: item.projectId,
      en_project_name: item.enProjectName,
      fr_project_name: item.frProjectName,
      en_project_description: item.enProjectDescription,
      fr_project_description: item.frProjectDescription
    });

    setAddOpen(true);
  };

  const handleDelete = (item: IProjectData) => {
    setConfirmTitle(t('home.confirmProjectDeletion'));
    setConfirmMessage(
      `${t('home.doYouWishToDeleteProject')}: ${item.projectName}`
    );
    setConfirmOpen(true);
    setActiveProject({
      id: item.projectId,
      en_project_name: item.enProjectName,
      fr_project_name: item.frProjectName,
      en_project_description: item.enProjectDescription,
      fr_project_description: item.frProjectDescription
    });
  };

  const handleConfirmDelete = () => {
    setConfirmOpen(false);
    onDeleteProject(activeProject?.id as number);
  };

  const handleAddProject = () => {
    setActiveProject({
      en_project_name: '',
      fr_project_name: '',
      en_project_description: '',
      fr_project_description: ''
    });
    setAddOpen(true);
  };

  async function handleSaveProject(data: any) {
    if (data.id) {
      onUpdateProject(data);
    } else {
      onCreateProject(data);
    }

    setAddOpen(false);
  }

  const handleCancelProject = () => {
    setActiveProject({
      en_project_name: '',
      fr_project_name: '',
      en_project_description: '',
      fr_project_description: ''
    });
    setAddOpen(false);
  };

  const handleResetPassword = (values: ResetPassword) => {
    onResetPassword(values);
    setAddOpen(false);
    navigate('/');
  };

  const handleForgotPassword = () => {
    setLoginOpen(false);
    setForgotOpen(true);
  };

  const handleCancelForgotPassword = () => {
    setLoginOpen(true);
    setForgotOpen(false);
  };

  const handleSubmitForgotPassword = (email: string) => {
    onForgotPassword(email);
    setForgotOpen(false);
    toast({
      type: 'info',
      icon: 'envelope',
      title: t('LoginModal.forgotPassword'),
      description: t('home.forgotPasswordMessage'),
      animation: 'bounce',
      time: 5000
    });
  };

  const handleSubmitProjectImage = (projectId: number, imageURL: string) => {
    onSaveProjectImage(projectId, imageURL);
  };

  const viewAction = {
    color: 'green',
    label: t('home.view'),
    icon: <Icon name='external alternate' />,
    onClick: (item: IProjectData) => onGotoProject(item.projectId)
  };

  const editAction = {
    label: t('home.edit'),
    icon: <Icon name='edit outline' />,
    onClick: (item: IProjectData) => handleEdit(item)
  };

  const deleteAction = {
    color: 'red',
    label: t('home.delete'),
    icon: <Icon name='delete' />,
    onClick: (item: IProjectData) => handleDelete(item)
  };

  const crumbs: ICrumb[] = [
    {
      key: 'projects',
      route: '/',
      content: t('home.projects'),
      active: true,
      onClick: () => {
      }
    }
  ];

  let actions: any[] = [viewAction];
  if (projectManager) {
    actions.push(editAction, deleteAction);
  }

  return (
    <Page crumbs={crumbs}>
      <SemanticToastContainer />
      {isAuthenticated() ? (
        <>
          <ContentBox widthPct={100} heightPct={80} padding={32}>
            <FlexColumn style={{ height: '100%', overflowY: 'auto' }}>
              {systemAdmin &&
                <FlexRow padBottom={10}>
                  <Button onClick={handleAddProject} color='green'>
                    <Icon name='add' />
                    {t('home.add')}
                  </Button>
                </FlexRow>
              }
              <Item.Group>
                {projects.map((project) => {
                  return (
                    <Item key={project.projectId}>
                      <ProjectImage projectId={project.projectId} image={project.projectImage} canEdit={projectManager}
                                    onSubmit={handleSubmitProjectImage} />
                      <Item.Content>
                        <Item.Header>{project.projectName}</Item.Header>
                        <Item.Description>
                          {project.projectDescription}
                        </Item.Description>
                        <Item.Extra>
                          <FlexRow width={300}>
                            {actions.map((action, idx) => {
                              return (
                                <div key={idx}>
                                  <Button
                                    color={action.color}
                                    onClick={() => action.onClick(project)}
                                  >
                                    {action.icon}
                                    {action.label}
                                  </Button>
                                </div>
                              );
                            })}
                          </FlexRow>
                        </Item.Extra>
                      </Item.Content>
                    </Item>
                  );
                })}
              </Item.Group>
            </FlexColumn>
          </ContentBox>
        </>
      ) : (
        <>
          <Message
            warning
            icon='mail'
            header={t('home.loginRequired')}
            content={t('home.loginRequiredMessage')}
          />
          <FlexRow padTop={20}>
            <Button onClick={() => setLoginOpen(true)} color='green'>
              {t('home.loginNow')}
            </Button>
          </FlexRow>
        </>
      )}
      {resetMessage ? (
        <ResetPasswordModal
          initialValues={{
            email: '',
            password: '',
            password_confirmation: ''
          }}
          onCancel={() => setLoginOpen(false)}
          onSubmit={handleResetPassword}
          open={loginOpen}
          resetMessage={resetMessage}
        />
      ) : (
        <LoginModal
          open={loginOpen}
          onPasswordChange={setPassword}
          onUsernameChange={setEmail}
          placeholder={t('home.email')}
          onLogin={handleLogin}
          loginFailed={loginFailed}
          onForgotPassword={handleForgotPassword}
        />
      )}
      <AddProjectModal
        open={addOpen}
        initialValues={activeProject as AddProject}
        onSubmit={handleSaveProject}
        onCancel={handleCancelProject}
      />
      <ConfirmationModal
        title={confirmTitle}
        message={confirmMessage}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
      <ForgotPasswordModal
        open={forgotOpen}
        onSubmit={handleSubmitForgotPassword}
        onCancel={handleCancelForgotPassword}
      />
    </Page>
  );
};

export default Home;
